import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleDollarSign, FileCheck, History, Info, Percent, Receipt, TriangleAlert, Truck } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Janela, Sobreposicao } from '@/componentes/JanelaDeAcao';
import { MaisAcoes } from '@/componentes/MaisAcoes';
import { Seletor } from '@/componentes/Seletor';
import { RodapeDeContagem, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarDecimal, formatarDia, formatarReais, interpretarNumero } from '@/lib/formato';
import { EXEMPLO_DO_MOTIVO, faltaNoMotivo } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDoOrcamento, type DetalheDaVersao, type Direcionador, type LinhaDoOrcamento, type Natureza, type Opcoes, type PremissaDaVersao, type Versoes } from './api';
import {
  corpoDaLinha,
  detalheDaAprovacao,
  detalheDaLinhaRemovida,
  detalheDaPremissa,
  detalheDaRevisao,
  detalheDaSugestaoAplicada,
  detalheDasLinhasGravadas,
  detalheDaVersaoCriada,
  detalheDoDescarte,
  faltaNaLinha,
  INVESTIMENTO,
  MES_VAZIO,
  mesCurto,
  NOME_DA_NATUREZA,
  NOME_DA_ORIGEM_DA_LINHA,
  NOME_DA_SITUACAO,
  NOME_DO_DIRECIONADOR,
  premissaEscrita,
  reaisComSinal,
  repetirNoAno,
  UNIDADE_DO_DIRECIONADOR,
  valorDoMes,
  valoresDaLinhaGravada,
  type ValoresDaLinha,
} from './orcamento';
import type { AcaoDoCabecalho } from './PaginaOrcamento';
import { SeloDaVersao } from './Selo';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

type Semestre = '1' | '2';

/**
 * FERRAMENTA 1 DO MÓDULO 19 — o orçamento por centro e conta, a versão, as
 * premissas com fonte e a aprovação.
 *
 * A grade mostra seis meses por vez (p.27: até oito colunas), e no rascunho a
 * linha de valor fixo se edita na própria célula; com a grade alterada, a
 * barra de salvar flutua no pé (p.39). A linha por horas ou por litros, e a
 * linha nova, abrem na gaveta (p.31: mais de seis campos).
 */
export function OrcamentoPorCentro({
  versoes,
  carregandoVersoes,
  erroDasVersoes,
  aoTentarDeNovo,
  versaoId,
  aoEscolherVersao,
  acao,
  aoFecharAcao,
  aoMudarAlteracoes,
}: {
  readonly versoes: Versoes | null;
  readonly carregandoVersoes: boolean;
  readonly erroDasVersoes: unknown;
  readonly aoTentarDeNovo: () => void;
  readonly versaoId: string | null;
  readonly aoEscolherVersao: (id: string) => void;
  readonly acao: AcaoDoCabecalho;
  readonly aoFecharAcao: () => void;
  readonly aoMudarAlteracoes: (com: boolean) => void;
}) {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const grava = permissoes.includes('orcamento.gravar');
  const aprova = permissoes.includes('orcamento.aprovar');
  const cliente = useQueryClient();
  const detalhe = useQuery({
    queryKey: ['orcamento', 'versao', versaoId],
    queryFn: () => apiDoOrcamento.versao(versaoId ?? ''),
    enabled: versaoId !== null,
    meta: { oQueLia: 'a versão do orçamento' },
  });
  const opcoes = useQuery({ queryKey: ['orcamento', 'opcoes'], queryFn: apiDoOrcamento.opcoes, staleTime: 60_000, enabled: grava });
  const [semestre, setSemestre] = useState<Semestre>(() => (new Date().getMonth() >= 6 ? '2' : '1'));
  const [centro, setCentro] = useState('');
  const [editando, setEditando] = useState<LinhaDoOrcamento | 'nova' | null>(null);
  const [janela, setJanela] = useState<'sugestao' | 'aprovar' | 'descartar' | 'outra-versao' | null>(null);
  const [premissa, setPremissa] = useState<PremissaDaVersao | null>(null);
  /* A grade alterada: linha -> competência -> o valor digitado. */
  const [alteracoes, setAlteracoes] = useState<Record<string, Record<string, string>>>({});
  const totalAlterado = Object.keys(alteracoes).length;
  useEffect(() => {
    aoMudarAlteracoes(totalAlterado > 0);
  }, [totalAlterado, aoMudarAlteracoes]);
  useEffect(() => {
    setAlteracoes({});
  }, [versaoId]);

  const d = detalhe.data;
  const rascunho = d?.versao.situacao === 'rascunho';
  const editavel = rascunho && grava;
  const recarregar = () => cliente.invalidateQueries({ queryKey: ['orcamento'] });

  const revisar = useMutation({
    mutationFn: (v: string) => apiDoOrcamento.revisar(v),
    meta: { retorno: retornoDe<{ exercicio: number; versao: number; nome: string; substitui: number; linhas: number }>((r) => ({ titulo: 'Revisão do orçamento aberta', ...detalheDaRevisao(r) })) },
    onSuccess: async (r) => {
      await recarregar();
      aoEscolherVersao(r.id);
    },
  });
  const salvarGrade = useMutation({
    mutationFn: (linhas: Parameters<typeof apiDoOrcamento.gravarLinhas>[1]) => apiDoOrcamento.gravarLinhas(versaoId ?? '', linhas),
    meta: { retorno: retornoDe<{ exercicio: number; versao: number; gravadas: number; linhas: { memoria: string }[]; total: number }>((r) => ({ titulo: 'Linhas do orçamento gravadas', ...detalheDasLinhasGravadas(r) })) },
    onSuccess: async () => {
      setAlteracoes({});
      await recarregar();
    },
  });

  const competencias = d?.competencias ?? [];
  const doSemestre = competencias.slice(semestre === '1' ? 0 : 6, semestre === '1' ? 6 : 12);
  const linhas = useMemo(() => (d?.linhas ?? []).filter((l) => centro === '' || l.centroId === centro), [d, centro]);
  const centrosDaVersao = useMemo(
    () => [...new Map((d?.linhas ?? []).map((l) => [l.centroId, { id: l.centroId, rotulo: `${l.centroCodigo} · ${l.centroNome}` }])).values()],
    [d],
  );
  const naturezaDaConta = (contaId: string): Natureza | null => opcoes.data?.contas.find((c) => c.id === contaId)?.natureza ?? null;

  const gravarAlteracoes = () => {
    if (!d) return;
    const corpo = Object.entries(alteracoes).flatMap(([linhaId, meses]) => {
      const l = d.linhas.find((x) => x.id === linhaId);
      if (!l) return [];
      const v = valoresDaLinhaGravada(l);
      const novos = { ...v.meses };
      for (const [c, valor] of Object.entries(meses)) novos[c] = { ...MES_VAZIO, valor };
      return [corpoDaLinha({ ...v, meses: novos }, competencias, () => l.natureza)];
    });
    salvarGrade.mutate(corpo);
  };

  const versaoAtual = versoes?.versoes.find((v) => v.id === versaoId) ?? null;

  if (erroDasVersoes) return <ErroDaLista oQue="As versões do orçamento" erro={erroDasVersoes} aoTentarDeNovo={aoTentarDeNovo} />;
  if (!carregandoVersoes && versoes && versoes.versoes.length === 0) {
    return (
      <>
        <EstadoVazio
          icone={CircleDollarSign}
          titulo="Nenhuma versão do orçamento"
          descricao={grava
            ? `Crie a versão de ${String(versoes.exercicioCorrente)}: monte as linhas por centro e conta, ou comece pela média do histórico, e peça a aprovação a outra pessoa.`
            : 'Ninguém montou o orçamento ainda. Peça a quem tem o perfil Controladoria para montar a versão do exercício.'}
        />
        <JanelaDaVersao aberta={acao === 'versao'} exercicio={versoes.exercicioCorrente} aoFechar={aoFecharAcao} aoCriar={aoEscolherVersao} />
      </>
    );
  }

  return (
    <>
      <section className="flex flex-wrap items-end gap-4" aria-label="Versão do orçamento">
        <div className="w-80 max-w-full">
          {versoes ? (
            <CampoSelecao
              rotulo="Versão"
              valor={versaoId ?? ''}
              aoMudar={aoEscolherVersao}
              opcoes={versoes.versoes.map((v) => ({ valor: v.id, rotulo: `${String(v.exercicio)} · versão ${String(v.versao)} · ${NOME_DA_SITUACAO[v.situacao]}` }))}
            />
          ) : (
            <Carregando oQue="Carregando as versões">
              <Barra className="h-10 w-full" />
            </Carregando>
          )}
        </div>
        {versaoAtual ? <SeloDaVersao situacao={versaoAtual.situacao} /> : null}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {editavel ? (
            <Botao nivel="secundario" onClick={() => { setJanela('sugestao'); }}>
              <History aria-hidden="true" className="size-4" />
              Sugerir do histórico
            </Botao>
          ) : null}
          {d?.versao.situacao === 'aprovado' && grava ? (
            <Botao nivel="secundario" disabled={revisar.isPending} onClick={() => { revisar.mutate(d.versao.id); }}>
              {revisar.isPending ? 'Abrindo…' : 'Abrir revisão'}
            </Botao>
          ) : null}
          {rascunho && aprova ? (
            <Botao nivel="secundario" onClick={() => { setJanela('aprovar'); }}>
              <FileCheck aria-hidden="true" className="size-4" />
              Aprovar versão
            </Botao>
          ) : null}
          {grava ? (
            <MaisAcoes
              acoes={[
                { rotulo: 'Criar versão de outro exercício', aoEscolher: () => { setJanela('outra-versao'); } },
                ...(rascunho ? [{ rotulo: 'Descartar rascunho', aoEscolher: () => { setJanela('descartar'); } }] : []),
              ]}
            />
          ) : null}
        </div>
      </section>
      {revisar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{explicarFalha(revisar.error).comoResolver}</p> : null}

      {d ? <QuemMontou detalhe={d} /> : null}

      {detalhe.isError && !d ? (
        <ErroDaLista oQue="As linhas do orçamento" erro={detalhe.error} aoTentarDeNovo={() => void detalhe.refetch()} />
      ) : (
        <>
          <FaixaIndicadores>
            <CardIndicador rotulo="Receita orçada" icone={Receipt} tom="marca" carregando={!d} valor={d ? formatarReais(d.totais.porNatureza.receita) : ''} contexto={d ? `Exercício de ${String(d.versao.exercicio)}` : 'Exercício'} />
            <CardIndicador
              rotulo="Custos e despesas"
              icone={CircleDollarSign}
              tom="neutro"
              carregando={!d}
              valor={d ? formatarReais(d.totais.porNatureza.custo + d.totais.porNatureza.despesa) : ''}
              contexto={d ? `Custo ${formatarReais(d.totais.porNatureza.custo)} · despesa ${formatarReais(d.totais.porNatureza.despesa)}` : 'Custo e despesa'}
            />
            <CardIndicador
              rotulo="Resultado orçado"
              icone={Percent}
              tom="operando"
              carregando={!d}
              valor={d ? reaisComSinal(d.totais.porNatureza.resultado) : ''}
              alerta={(d?.totais.porNatureza.resultado ?? 0) < 0}
              contexto="Receita menos custos e despesas"
            />
            <CardIndicador rotulo="Investimento (CAPEX)" icone={Truck} tom="disponivel" carregando={!d} valor={d ? formatarReais(d.totais.porNatureza.investimento) : ''} contexto="Fora do resultado; entra no caixa" />
          </FaixaIndicadores>

          <TabelaDasPremissas detalhe={d} carregando={detalhe.isPending} editavel={editavel} aoEditar={setPremissa} />

          <div className="flex flex-wrap items-end gap-4">
            <h2 className="txt-secao text-tinta">Linhas por centro e conta</h2>
            <div className="ml-auto flex flex-wrap items-end gap-4">
              <div className="w-64">
                <CampoSelecao
                  rotulo="Centro de custo"
                  valor={centro}
                  aplicado={centro !== ''}
                  aoMudar={setCentro}
                  rotuloVazio="Todos os centros"
                  opcoes={centrosDaVersao.map((c) => ({ valor: c.id, rotulo: c.rotulo }))}
                />
              </div>
              <Seletor<Semestre>
                rotulo="Semestre"
                valor={semestre}
                aoEscolher={setSemestre}
                opcoes={[{ valor: '1', rotulo: 'Jan a jun' }, { valor: '2', rotulo: 'Jul a dez' }]}
              />
            </div>
          </div>
          <Tabela
            rotulo="Linhas do orçamento por centro e conta"
            colunas={colunasDaGrade(doSemestre, editavel, alteracoes, (linha, competencia, valor) => {
              setAlteracoes((a) => ({ ...a, [linha]: { ...(a[linha] ?? {}), [competencia]: valor } }));
            })}
            linhas={linhas}
            chaveDaLinha={(l) => l.id}
            carregando={detalhe.isPending || (versaoId !== null && !d)}
            alturaDupla
            {...(editavel ? { aoSelecionar: (l: LinhaDoOrcamento) => { setEditando(l); } } : {})}
            vazio={
              centro !== '' ? (
                <EstadoVazio
                  titulo="Nenhuma linha neste centro"
                  descricao="Este centro não tem linha na versão. Escolha outro centro ou veja todos."
                  acao={<Botao nivel="secundario" onClick={() => { setCentro(''); }}>Ver todos os centros</Botao>}
                />
              ) : (
                <EstadoVazio
                  titulo="Nenhuma linha nesta versão"
                  descricao={editavel
                    ? 'Adicione as linhas por centro e conta, ou use a sugestão do histórico: ela traz a média dos meses realizados, e você confere antes de entrar.'
                    : 'Esta versão foi aprovada sem linha. Abra uma revisão para montar o orçamento.'}
                />
              )
            }
            rodape={<RodapeDeContagem total={linhas.length} />}
          />
        </>
      )}

      {editavel && totalAlterado > 0 ? (
        <div className="barra-de-acoes">
          <span className="txt-apoio mr-auto text-tinta-secundaria">{`${String(totalAlterado)} ${totalAlterado === 1 ? 'linha alterada' : 'linhas alteradas'}`}</span>
          {salvarGrade.isError ? <span role="alert" className="txt-apoio text-parada-texto">{explicarFalha(salvarGrade.error).comoResolver}</span> : null}
          <Botao nivel="terciario" onClick={() => { setAlteracoes({}); salvarGrade.reset(); }}>
            Descartar alterações
          </Botao>
          <Botao nivel="primario" disabled={salvarGrade.isPending} onClick={gravarAlteracoes}>
            {salvarGrade.isPending ? 'Gravando…' : `Salvar ${String(totalAlterado)} ${totalAlterado === 1 ? 'linha' : 'linhas'}`}
          </Botao>
        </div>
      ) : null}

      {d && editavel && opcoes.data ? (
        <GavetaDaLinha
          key={editando === null ? 'fechada' : editando === 'nova' ? 'nova' : editando.id}
          versaoId={d.versao.id}
          competencias={competencias}
          opcoes={opcoes.data}
          linha={acao === 'linha' ? 'nova' : editando}
          aoFechar={() => {
            setEditando(null);
            aoFecharAcao();
          }}
          naturezaDaConta={naturezaDaConta}
        />
      ) : null}
      {d && editavel ? (
        <JanelaDaSugestao aberta={janela === 'sugestao'} versaoId={d.versao.id} aoFechar={() => { setJanela(null); }} />
      ) : null}
      {d && rascunho && aprova ? <JanelaDaAprovacao aberta={janela === 'aprovar'} detalhe={d} aoFechar={() => { setJanela(null); }} /> : null}
      {d && rascunho ? (
        <JanelaDoDescarte
          aberta={janela === 'descartar'}
          detalhe={d}
          aoFechar={() => { setJanela(null); }}
          aoDescartar={() => { if (versoes?.atual && versoes.atual !== d.versao.id) aoEscolherVersao(versoes.atual); }}
        />
      ) : null}
      {d && editavel && premissa ? (
        <JanelaDaPremissa key={premissa.codigo} versaoId={d.versao.id} premissa={premissa} aoFechar={() => { setPremissa(null); }} />
      ) : null}
      {versoes ? (
        <JanelaDaVersao
          aberta={janela === 'outra-versao' || acao === 'versao'}
          exercicio={Math.max(versoes.exercicioCorrente, ...versoes.versoes.map((v) => v.exercicio)) + (versoes.versoes.some((v) => v.exercicio === versoes.exercicioCorrente) ? 1 : 0)}
          aoFechar={() => { setJanela(null); aoFecharAcao(); }}
          aoCriar={aoEscolherVersao}
        />
      ) : null}
    </>
  );
}

/** Quem montou, quem aprovou e com que nota — a trilha da versão, que não se edita (p.44). */
function QuemMontou({ detalhe }: { readonly detalhe: DetalheDaVersao }) {
  const v = detalhe.versao;
  return (
    <p className="txt-apoio text-pretty text-tinta-secundaria">
      {[
        `${v.nome}`,
        `montada por ${v.montadoPor ?? 'quem saiu da empresa'} em ${formatarDia(v.criadoEm)}`,
        v.substituiVersao !== null ? `revisão da versão ${String(v.substituiVersao)}` : null,
        v.aprovadoPor ? `aprovada por ${v.aprovadoPor} em ${formatarDia(v.aprovadoEm)}: “${v.nota ?? ''}”` : null,
        v.situacao === 'arquivado' ? `arquivada em ${formatarDia(v.arquivadoEm)}` : null,
      ].filter(Boolean).join(' · ')}
    </p>
  );
}

/* ============================================================ a grade */

function colunasDaGrade(
  meses: readonly string[],
  editavel: boolean,
  alteracoes: Readonly<Record<string, Readonly<Record<string, string>>>>,
  aoAlterar: (linha: string, competencia: string, valor: string) => void,
): readonly Coluna<LinhaDoOrcamento>[] {
  const valorDe = (l: LinhaDoOrcamento, c: string) => l.valores.find((v) => v.competencia === c);
  return [
    {
      chave: 'linha',
      rotulo: 'Centro e conta',
      prioridade: 'chave',
      celula: (l) => (
        <span className="flex min-w-72 flex-col">
          <span className="txt-corpo text-tinta">
            <span className="codigo">{l.centroCodigo}</span>
            {` · ${l.contaCodigo ? `${l.contaCodigo} ${l.contaNome ?? ''}` : 'Investimento'}`}
          </span>
          <TextoDaCelula
            texto={`${NOME_DA_NATUREZA[l.natureza]} · ${NOME_DO_DIRECIONADOR[l.direcionador]}${l.contratoCodigo ? ` · ${l.contratoCodigo}` : ''}${l.equipamentoCodigo ? ` · ${l.equipamentoCodigo}` : ''} · ${l.memoria}${l.origem === 'manual' ? '' : ` · ${NOME_DA_ORIGEM_DA_LINHA[l.origem]}`}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </span>
      ),
    },
    ...meses.map((c): Coluna<LinhaDoOrcamento> => ({
      chave: c,
      rotulo: mesCurto(c),
      alinhamento: 'numero',
      celula: (l) => {
        const v = valorDe(l, c);
        const alterado = alteracoes[l.id]?.[c];
        if (editavel && l.direcionador === 'fixo') {
          return (
            <input
              aria-label={`${l.centroCodigo} ${l.contaCodigo ?? 'investimento'}, ${mesCurto(c)}`}
              inputMode="decimal"
              className={`controle h-8 w-28 text-right tabular-nums ${alterado !== undefined ? 'border-marca bg-marca-fundo' : ''}`}
              value={alterado ?? (v ? formatarDecimal(v.valor, 2) : '')}
              onClick={(e) => { e.stopPropagation(); }}
              onKeyDown={(e) => { e.stopPropagation(); }}
              onChange={(e) => { aoAlterar(l.id, c, e.target.value); }}
            />
          );
        }
        return v ? (
          <span className="flex flex-col items-end">
            <span className="numero whitespace-nowrap text-tinta">{formatarReais(v.valor)}</span>
            {v.quantidade !== null && l.direcionador !== 'fixo' ? (
              <span className="txt-apoio whitespace-nowrap text-tinta-apagada">{`${String(v.quantidade).replace('.', ',')} ${UNIDADE_DO_DIRECIONADOR[l.direcionador].quantidade}`}</span>
            ) : null}
          </span>
        ) : (
          <span className="txt-apoio text-tinta-apagada">—</span>
        );
      },
    })),
    {
      chave: 'total',
      rotulo: 'Total do ano',
      alinhamento: 'numero',
      celula: (l) => <span className="numero whitespace-nowrap font-semibold text-tinta">{formatarReais(l.total)}</span>,
    },
  ];
}

/* ============================================================ as premissas */

function TabelaDasPremissas({
  detalhe,
  carregando,
  editavel,
  aoEditar,
}: {
  readonly detalhe: DetalheDaVersao | undefined;
  readonly carregando: boolean;
  readonly editavel: boolean;
  readonly aoEditar: (p: PremissaDaVersao) => void;
}) {
  const colunas: readonly Coluna<PremissaDaVersao>[] = [
    {
      chave: 'premissa',
      rotulo: 'Premissa',
      prioridade: 'chave',
      celula: (p) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{p.rotulo}</span>
          <TextoDaCelula texto={p.ondeEntra} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'valor',
      rotulo: 'Valor',
      alinhamento: 'numero',
      celula: (p) => (
        <span className={`numero whitespace-nowrap ${p.valor === null ? 'text-atencao-texto' : 'text-tinta'}`}>{premissaEscrita(p.codigo, p.valor)}</span>
      ),
    },
    {
      chave: 'fonte',
      rotulo: 'Fonte',
      celula: (p) => (
        <TextoDaCelula
          texto={p.fonte ? `${p.fonte}${p.origem === 'historico' ? ' (do histórico)' : ''}` : 'Sem fonte: a premissa não foi informada'}
          classe={`txt-apoio ${p.fonte ? 'text-tinta' : 'text-tinta-secundaria'}`}
          linhas={2}
        />
      ),
    },
    {
      chave: 'quem',
      rotulo: 'Quem informou',
      prioridade: 'secundaria',
      celula: (p) => <span className="txt-apoio text-tinta-secundaria">{p.gravadoPor ? `${p.gravadoPor} · ${formatarDia(p.atualizadoEm)}` : '—'}</span>,
    },
  ];
  return (
    <Tabela
      rotulo="Premissas da versão"
      colunas={colunas}
      linhas={detalhe?.premissas ?? []}
      chaveDaLinha={(p) => p.codigo}
      carregando={carregando}
      alturaDupla
      {...(editavel ? { aoSelecionar: aoEditar, menu: (p: PremissaDaVersao) => <MaisAcoes forma="icone" acoes={[{ rotulo: 'Editar premissa', aoEscolher: () => { aoEditar(p); } }]} /> } : {})}
      vazio={<EstadoVazio titulo="Nenhuma premissa" descricao="As premissas vêm com a versão. Recarregue a tela; se continuar vazio, avise o suporte." />}
      rodape={<RodapeDeContagem total={detalhe?.premissas.length ?? 0} />}
    />
  );
}

function JanelaDaPremissa({ versaoId, premissa, aoFechar }: { readonly versaoId: string; readonly premissa: PremissaDaVersao; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [valor, setValor] = useState(premissa.valor === null ? '' : String(premissa.valor).replace('.', ','));
  const [fonte, setFonte] = useState(premissa.fonte ?? '');
  const [origem, setOrigem] = useState<'historico' | 'informada'>(premissa.origem ?? 'informada');
  const [tentou, setTentou] = useState(false);
  const numero = interpretarNumero(valor);
  const faltaValor = numero === null ? `Informe o valor em ${premissa.unidade}.` : premissa.codigo === 'utilizacao' && numero > 100 ? 'A utilização vai até 100%.' : null;
  const faltaFonte = fonte.trim().length < 5 ? 'Escreva a fonte, por exemplo "cotação da distribuidora de 01/10/2026".' : null;
  const gravar = useMutation({
    mutationFn: () => apiDoOrcamento.gravarPremissa(versaoId, premissa.codigo, numero ?? 0, fonte, origem),
    meta: { retorno: retornoDe<{ codigo: PremissaDaVersao['codigo']; valor: number; fonte: string; antes: number | null }>((r) => ({ titulo: 'Premissa do orçamento gravada', ...detalheDaPremissa(r, premissa.rotulo) })) },
    onSuccess: async () => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['orcamento'] });
    },
  });
  return (
    <Janela
      titulo={premissa.rotulo}
      descricao={`Entra ${premissa.ondeEntra}.`}
      aberta
      aoFechar={aoFechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={gravar.isPending} onClick={() => { setTentou(true); if (!faltaValor && !faltaFonte) gravar.mutate(); }}>
            {gravar.isPending ? 'Gravando…' : 'Gravar premissa'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {gravar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(gravar.error).erro}. ${explicarFalha(gravar.error).comoResolver}`}</p> : null}
        {premissa.sugestao ? (
          <div className="bloco-rebaixado flex flex-wrap items-center gap-3 rounded-controle p-4">
            <Info aria-hidden="true" className="size-4 shrink-0 text-informacao" />
            <span className="txt-apoio min-w-0 flex-1 text-tinta">{`O histórico sugere ${premissaEscrita(premissa.codigo, premissa.sugestao.valor)}: ${premissa.sugestao.fonte}.`}</span>
            <Botao
              nivel="fantasma"
              tamanho="p"
              onClick={() => {
                if (!premissa.sugestao) return;
                setValor(String(premissa.sugestao.valor).replace('.', ','));
                setFonte(premissa.sugestao.fonte);
                setOrigem('historico');
              }}
            >
              Usar a sugestão
            </Botao>
          </div>
        ) : null}
        <CampoTexto rotulo={`Valor (${premissa.unidade})`} valor={valor} aoMudar={(v) => { setValor(v); setOrigem('informada'); }} validar={() => (tentou ? faltaValor : null)} maxLength={16} />
        <CampoTexto
          rotulo="Fonte"
          valor={fonte}
          aoMudar={(v) => { setFonte(v); setOrigem('informada'); }}
          placeholder="Cotação da distribuidora de 01/10/2026"
          ajuda="De onde vem o número. Premissa sem fonte é palpite."
          validar={() => (tentou ? faltaFonte : null)}
          maxLength={200}
        />
      </div>
    </Janela>
  );
}

/* ============================================================ a linha */

function GavetaDaLinha({
  versaoId,
  competencias,
  opcoes,
  linha,
  aoFechar,
  naturezaDaConta,
}: {
  readonly versaoId: string;
  readonly competencias: readonly string[];
  readonly opcoes: Opcoes;
  readonly linha: LinhaDoOrcamento | 'nova' | null;
  readonly aoFechar: () => void;
  readonly naturezaDaConta: (contaId: string) => Natureza | null;
}) {
  const cliente = useQueryClient();
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const base = useId();
  const existente = linha !== null && linha !== 'nova' ? linha : null;
  const [v, setV] = useState<ValoresDaLinha>(() =>
    existente ? valoresDaLinhaGravada(existente) : { id: null, centroCustoId: '', contaId: '', contratoId: '', equipamentoId: '', direcionador: 'fixo', memoria: '', meses: {} },
  );
  const [tentou, setTentou] = useState(false);
  /* Remover pede confirmação (p.30): o rodapé vira a pergunta, com o alvo. */
  const [confirmando, setConfirmando] = useState(false);
  const falta = faltaNaLinha(v, competencias);
  const recarregar = () => cliente.invalidateQueries({ queryKey: ['orcamento'] });
  const gravar = useMutation({
    mutationFn: () => apiDoOrcamento.gravarLinhas(versaoId, [corpoDaLinha(v, competencias, naturezaDaConta)]),
    meta: { retorno: retornoDe<{ exercicio: number; versao: number; gravadas: number; linhas: { memoria: string }[]; total: number }>((r) => ({ titulo: existente ? 'Linha do orçamento alterada' : 'Linha do orçamento criada', ...detalheDasLinhasGravadas(r) })) },
    onSuccess: async () => {
      aoFechar();
      await recarregar();
    },
  });
  const remover = useMutation({
    mutationFn: () => apiDoOrcamento.removerLinha(versaoId, existente?.id ?? ''),
    meta: { retorno: retornoDe<{ memoria: string; centro: string; conta: string | null; total: number }>((r) => ({ titulo: 'Linha removida do rascunho', ...detalheDaLinhaRemovida(r) })) },
    onSuccess: async () => {
      aoFechar();
      await recarregar();
    },
  });
  const mes = (c: string) => v.meses[c] ?? MES_VAZIO;
  const mudarMes = (c: string, campo: 'quantidade' | 'preco' | 'valor', texto: string) => {
    setV({ ...v, meses: { ...v.meses, [c]: { ...mes(c), [campo]: texto } } });
  };
  const total = competencias.reduce((t, c) => t + Math.round((valorDoMes(v.direcionador, mes(c)) ?? 0) * 100), 0) / 100;
  const recusa = gravar.error ?? remover.error;
  const unidades = v.direcionador === 'fixo' ? null : UNIDADE_DO_DIRECIONADOR[v.direcionador];

  return (
    <Sobreposicao
      lugar="direita"
      titulo={existente ? 'Editar linha do orçamento' : 'Adicionar linha do orçamento'}
      descricao="Centro, conta e o valor de cada mês. Com direcionador, quantidade vezes preço."
      aberta={linha !== null}
      aoFechar={aoFechar}
      acoes={
        confirmando && existente ? (
          <>
            <span className="txt-apoio mr-auto text-pretty text-tinta">{`Remover a linha ${existente.centroCodigo} · ${existente.memoria}? Os ${formatarReais(existente.total)} saem do rascunho.`}</span>
            <Botao nivel="terciario" onClick={() => { setConfirmando(false); }}>Manter linha</Botao>
            <Botao nivel="destrutivo" disabled={remover.isPending} onClick={() => { remover.mutate(); }}>
              {remover.isPending ? 'Removendo…' : 'Remover linha'}
            </Botao>
          </>
        ) : (
        <>
          {existente ? (
            <Botao nivel="fantasma" onClick={() => { setConfirmando(true); }}>
              Remover linha
            </Botao>
          ) : null}
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={gravar.isPending}
            onClick={() => {
              setTentou(true);
              if (Object.keys(falta).length === 0) gravar.mutate();
            }}
          >
            {gravar.isPending ? 'Gravando…' : existente ? 'Salvar linha' : 'Adicionar linha'}
          </Botao>
        </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {recusa ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(recusa).erro}. ${explicarFalha(recusa).comoResolver}`}</p> : null}
        <CampoSelecao
          rotulo="Centro de custo"
          valor={v.centroCustoId}
          aoMudar={(c) => { setV({ ...v, centroCustoId: c }); }}
          rotuloVazio="Escolha o centro"
          opcoes={opcoes.centros.map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.nome}` }))}
        />
        {tentou && falta.centro ? <p className="txt-apoio text-parada-texto">{falta.centro}</p> : null}
        <CampoSelecao
          rotulo="Conta do plano gerencial"
          valor={v.contaId}
          aoMudar={(c) => { setV({ ...v, contaId: c, direcionador: c === INVESTIMENTO ? 'fixo' : v.direcionador }); }}
          rotuloVazio="Escolha a conta"
          opcoes={[
            ...opcoes.contas.map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.nome} (${NOME_DA_NATUREZA[c.natureza].toLocaleLowerCase('pt-BR')})` })),
            { valor: INVESTIMENTO, rotulo: 'Investimento (CAPEX), fora da DRE' },
          ]}
        />
        {tentou && falta.conta ? <p className="txt-apoio text-parada-texto">{falta.conta}</p> : null}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoSelecao
            rotulo="Contrato"
            opcional
            valor={v.contratoId}
            aoMudar={(c) => { setV({ ...v, contratoId: c }); }}
            rotuloVazio="Sem contrato"
            opcoes={opcoes.contratos.map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.descricao}` }))}
          />
          <CampoSelecao
            rotulo="Máquina"
            opcional
            valor={v.equipamentoId}
            aoMudar={(c) => { setV({ ...v, equipamentoId: c }); }}
            rotuloVazio="Sem máquina"
            opcoes={comVisualDaFrota(opcoes.equipamentos.map((e) => opcaoDaMaquina(e)))}
            adesivo
          />
        </div>
        {v.contaId !== INVESTIMENTO ? (
          <Seletor<Direcionador>
            rotulo="Direcionador"
            valor={v.direcionador}
            aoEscolher={(d) => { setV({ ...v, direcionador: d }); }}
            opcoes={[{ valor: 'fixo', rotulo: 'Valor fixo' }, { valor: 'horas', rotulo: 'Por horas' }, { valor: 'litros', rotulo: 'Por litros' }]}
          />
        ) : null}
        <CampoTexto
          rotulo="Memória de cálculo"
          valor={v.memoria}
          aoMudar={(m) => { setV({ ...v, memoria: m }); }}
          placeholder="Aluguel do pátio, contrato de 2026"
          ajuda="Como o número foi feito: quem lê o orçamento daqui a seis meses precisa disso."
          validar={() => (tentou ? (falta.memoria ?? null) : null)}
          maxLength={300}
        />
        <fieldset className="flex flex-col gap-2" aria-describedby={`${base}-meses`}>
          <legend className="txt-secao text-tinta">Valores do exercício</legend>
          <p id={`${base}-meses`} className="txt-apoio text-tinta-secundaria">
            {unidades ? `Quantidade em ${unidades.quantidade} e preço em ${unidades.preco}; o valor é a conta das duas.` : 'O valor de cada mês, em reais. Deixe em branco o mês sem valor.'}
          </p>
          <div className="flex flex-col divide-y divide-borda-sutil">
            {competencias.map((c, i) => (
              <div key={c} className="flex items-center gap-2 py-2">
                <span className="txt-corpo w-12 shrink-0 text-tinta-secundaria">{mesCurto(c)}</span>
                {unidades ? (
                  <>
                    <input aria-label={`Quantidade de ${mesCurto(c)}`} inputMode="decimal" className="controle h-10 w-24 text-right tabular-nums" value={mes(c).quantidade} onChange={(e) => { mudarMes(c, 'quantidade', e.target.value); }} />
                    <span className="txt-apoio text-tinta-apagada">×</span>
                    <input aria-label={`Preço de ${mesCurto(c)}`} inputMode="decimal" className="controle h-10 w-24 text-right tabular-nums" value={mes(c).preco} onChange={(e) => { mudarMes(c, 'preco', e.target.value); }} />
                    <span className="numero ml-auto whitespace-nowrap text-tinta">{formatarReais(valorDoMes(v.direcionador, mes(c)) ?? 0)}</span>
                  </>
                ) : (
                  <input aria-label={`Valor de ${mesCurto(c)}`} inputMode="decimal" className="controle h-10 w-40 text-right tabular-nums" value={mes(c).valor} onChange={(e) => { mudarMes(c, 'valor', e.target.value); }} />
                )}
                {i === 0 ? (
                  <Botao nivel="fantasma" tamanho="p" className={unidades ? '' : 'ml-auto'} onClick={() => { setV(repetirNoAno(v, competencias, c)); }}>
                    Repetir nos 12 meses
                  </Botao>
                ) : null}
              </div>
            ))}
          </div>
          {tentou && falta.meses ? <p className="txt-apoio text-parada-texto">{falta.meses}</p> : null}
          <p className="txt-corpo text-right text-tinta">{`Total do ano: ${formatarReais(total)}`}</p>
        </fieldset>
      </div>
    </Sobreposicao>
  );
}

/* ============================================================ as janelas da versão */

function JanelaDaVersao({ aberta, exercicio, aoFechar, aoCriar }: { readonly aberta: boolean; readonly exercicio: number; readonly aoFechar: () => void; readonly aoCriar: (id: string) => void }) {
  const cliente = useQueryClient();
  const [ano, setAno] = useState(String(exercicio));
  const [nome, setNome] = useState(`Orçamento ${String(exercicio)}`);
  useEffect(() => {
    if (aberta) {
      setAno(String(exercicio));
      setNome(`Orçamento ${String(exercicio)}`);
    }
  }, [aberta, exercicio]);
  const n = Number(ano);
  const faltaAno = !/^\d{4}$/.test(ano) || n < 2020 || n > 2100 ? 'Informe o exercício com quatro dígitos, por exemplo 2027.' : null;
  const criar = useMutation({
    mutationFn: () => apiDoOrcamento.criarVersao(n, nome),
    meta: { retorno: retornoDe<{ exercicio: number; versao: number; nome: string }>((r) => ({ titulo: 'Versão do orçamento criada', ...detalheDaVersaoCriada(r) })) },
    onSuccess: async (r) => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['orcamento'] });
      aoCriar(r.id);
    },
  });
  return (
    <Janela
      titulo="Criar versão do orçamento"
      descricao="A versão nasce em rascunho. Se o exercício já tem versão aprovada, o caminho é a revisão dela."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="p"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={criar.isPending || faltaAno !== null || nome.trim().length < 3} onClick={() => { criar.mutate(); }}>
            {criar.isPending ? 'Criando…' : 'Criar versão'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {criar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(criar.error).erro}. ${explicarFalha(criar.error).comoResolver}`}</p> : null}
        <CampoTexto rotulo="Exercício" valor={ano} aoMudar={setAno} validar={() => faltaAno} maxLength={4} />
        <CampoTexto rotulo="Nome" valor={nome} aoMudar={setNome} maxLength={80} />
      </div>
    </Janela>
  );
}

/** A APROVAÇÃO (p.44): a alçada de quem está logado à vista antes do botão, e a nota. */
function JanelaDaAprovacao({ aberta, detalhe, aoFechar }: { readonly aberta: boolean; readonly detalhe: DetalheDaVersao; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [nota, setNota] = useState('');
  const v = detalhe.versao;
  const faltaNota = nota.trim().length < 15 ? `Escreva a nota com pelo menos 15 caracteres (faltam ${String(Math.max(0, 15 - nota.trim().length))}).` : null;
  const aprovar = useMutation({
    mutationFn: () => apiDoOrcamento.aprovar(v.id, nota),
    meta: { retorno: retornoDe<{ exercicio: number; versao: number; nome: string; arquivada: number | null; receita: number; resultado: number }>((r) => ({ titulo: 'Orçamento aprovado', ...detalheDaAprovacao(r) })) },
    onSuccess: async () => {
      aoFechar();
      setNota('');
      await cliente.invalidateQueries({ queryKey: ['orcamento'] });
    },
  });
  return (
    <Janela
      titulo={`Aprovar o orçamento ${String(v.exercicio)}, versão ${String(v.versao)}?`}
      descricao="Aprovada, a versão não muda mais: corrigir é abrir uma revisão. A revisão aprovada arquiva a anterior."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Manter em rascunho</Botao>
          <Botao nivel="primario" disabled={!v.podeAprovar || faltaNota !== null || aprovar.isPending} onClick={() => { aprovar.mutate(); }}>
            {aprovar.isPending ? 'Aprovando…' : 'Aprovar orçamento'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className={`flex items-start gap-3 rounded-controle p-4 ${v.podeAprovar ? 'bloco-rebaixado' : 'bg-atencao-fundo'}`}>
          {v.podeAprovar ? <FileCheck aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-sucesso" /> : <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-atencao" />}
          <p className="txt-apoio text-pretty text-tinta">
            {v.podeAprovar
              ? `Você tem a licença de aprovar o orçamento e não montou esta versão (montada por ${v.montadoPor ?? 'outra pessoa'}).`
              : (v.impedimentoDeAprovar ?? 'Você não pode aprovar esta versão.')}
          </p>
        </div>
        <p className="txt-apoio text-tinta-secundaria">{`Receita ${formatarReais(detalhe.totais.porNatureza.receita)} · custos e despesas ${formatarReais(detalhe.totais.porNatureza.custo + detalhe.totais.porNatureza.despesa)} · resultado ${reaisComSinal(detalhe.totais.porNatureza.resultado)} · investimento ${formatarReais(detalhe.totais.porNatureza.investimento)}`}</p>
        {aprovar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(aprovar.error).erro}. ${explicarFalha(aprovar.error).comoResolver}`}</p> : null}
        <CampoTexto
          rotulo="Nota da aprovação"
          valor={nota}
          aoMudar={setNota}
          placeholder="Aprovado na reunião de diretoria de 06/10/2026"
          ajuda="Fica na versão e na trilha, com o seu nome."
          validar={() => (nota.length > 0 ? faltaNota : null)}
          maxLength={400}
        />
      </div>
    </Janela>
  );
}

function JanelaDoDescarte({ aberta, detalhe, aoFechar, aoDescartar }: { readonly aberta: boolean; readonly detalhe: DetalheDaVersao; readonly aoFechar: () => void; readonly aoDescartar: () => void }) {
  const cliente = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const v = detalhe.versao;
  const descartar = useMutation({
    mutationFn: () => apiDoOrcamento.descartar(v.id, motivo),
    meta: { retorno: retornoDe<{ exercicio: number; versao: number; nome: string }, unknown>((r) => ({ titulo: 'Rascunho do orçamento descartado', ...detalheDoDescarte(r, motivo) })) },
    onSuccess: async () => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['orcamento'] });
      aoDescartar();
    },
  });
  return (
    <Janela
      titulo={`Descartar o rascunho ${String(v.exercicio)}, versão ${String(v.versao)}?`}
      descricao={`As ${String(detalhe.linhas.length)} linhas vão ao arquivo com a versão; nada se apaga, e a trilha guarda o motivo.`}
      aberta={aberta}
      aoFechar={aoFechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Manter rascunho</Botao>
          <Botao nivel="destrutivo" disabled={faltaNoMotivo(motivo) !== null || descartar.isPending} onClick={() => { descartar.mutate(); }}>
            {descartar.isPending ? 'Descartando…' : 'Descartar rascunho'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {descartar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(descartar.error).erro}. ${explicarFalha(descartar.error).comoResolver}`}</p> : null}
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={EXEMPLO_DO_MOTIVO['orcamento.descartar']} />
      </div>
    </Janela>
  );
}

/** A SUGESTÃO DO HISTÓRICO, conferida antes de valer (p.43): o botão diz quanto entra. */
function JanelaDaSugestao({ aberta, versaoId, aoFechar }: { readonly aberta: boolean; readonly versaoId: string; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const sugestao = useQuery({ queryKey: ['orcamento', 'sugestao', versaoId], queryFn: () => apiDoOrcamento.sugestao(versaoId), enabled: aberta });
  const [escolhidas, setEscolhidas] = useState<ReadonlySet<string> | null>(null);
  const s = sugestao.data;
  const novas = (s?.sugestoes ?? []).filter((x) => !x.jaNoOrcamento);
  const marcadas = escolhidas ?? new Set(novas.map((x) => x.chave));
  const aplicar = useMutation({
    mutationFn: () => apiDoOrcamento.aplicarSugestao(versaoId, [...marcadas]),
    meta: { retorno: retornoDe<{ criadas: number; mesesNoHistorico: number; totalNoExercicio: number; exercicio: number }>((r) => ({ titulo: 'Sugestão do histórico incluída', ...detalheDaSugestaoAplicada(r) })) },
    onSuccess: async () => {
      aoFechar();
      setEscolhidas(null);
      await cliente.invalidateQueries({ queryKey: ['orcamento'] });
    },
  });
  const quantas = novas.filter((x) => marcadas.has(x.chave)).length;
  return (
    <Janela
      titulo="Sugerir do histórico"
      descricao={s ? `A média de ${String(s.competencias.length)} ${s.competencias.length === 1 ? 'mês realizado' : 'meses realizados'} por centro e conta, repetida nos 12 meses. Confira antes de incluir.` : 'A média dos meses realizados por centro e conta.'}
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={quantas === 0 || aplicar.isPending} onClick={() => { aplicar.mutate(); }}>
            {aplicar.isPending ? 'Incluindo…' : `Incluir ${String(quantas)} ${quantas === 1 ? 'linha' : 'linhas'}`}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {aplicar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(aplicar.error).erro}. ${explicarFalha(aplicar.error).comoResolver}`}</p> : null}
        {sugestao.isError ? (
          <ErroDaLista oQue="As sugestões do histórico" erro={sugestao.error} aoTentarDeNovo={() => void sugestao.refetch()} />
        ) : !s ? (
          <Carregando oQue="Carregando a sugestão do histórico" className="flex flex-col gap-2">
            <Barra className="h-4 w-full" />
            <Barra className="h-4 w-4/5" />
          </Carregando>
        ) : s.sugestoes.length === 0 ? (
          <EstadoVazio titulo="Nada a sugerir" descricao="O histórico não tem custo nem receita por centro e conta nos meses fechados. Monte as linhas à mão." />
        ) : (
          <ul className="flex flex-col divide-y divide-borda-sutil" aria-label="Linhas sugeridas">
            {s.sugestoes.map((x) => (
              <li key={x.chave} className="flex items-start gap-3 py-2">
                <input
                  type="checkbox"
                  className="mt-1 size-4"
                  aria-label={`${x.centro.codigo} ${x.conta.codigo}`}
                  disabled={x.jaNoOrcamento}
                  checked={!x.jaNoOrcamento && marcadas.has(x.chave)}
                  onChange={(e) => {
                    const nova = new Set(marcadas);
                    if (e.target.checked) nova.add(x.chave);
                    else nova.delete(x.chave);
                    setEscolhidas(nova);
                  }}
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="txt-corpo text-tinta"><span className="codigo">{x.centro.codigo}</span>{` · ${x.conta.codigo} ${x.conta.nome}`}</span>
                  <span className="txt-apoio text-tinta-secundaria">{x.jaNoOrcamento ? 'Já está no orçamento' : `Com valor em ${String(x.mesesComValor)} de ${String(x.mesesNoHistorico)} ${x.mesesNoHistorico === 1 ? 'mês' : 'meses'}`}</span>
                </span>
                <span className="numero whitespace-nowrap text-tinta">{`${formatarReais(x.mediaMensal)}/mês`}</span>
              </li>
            ))}
          </ul>
        )}
        {s && s.naoEntrou.length > 0 ? (
          <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">
            {s.naoEntrou.map((f) => <li key={f}>{f}</li>)}
          </ul>
        ) : null}
      </div>
    </Janela>
  );
}
